import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ArrowsAiFaqsAPI from 'dashboard/api/arrowsAi/faqs';
import {
  parseAPIErrorResponse,
  throwErrorMessage,
} from 'dashboard/store/utils/api';

const normalizeFaq = raw => ({
  id: Number(raw.id),
  question: raw.question || '',
  answer: raw.answer || '',
  createdBy: raw.created_by || raw.createdBy || '',
  createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
});

export function useArrowsAiFaqs(assistantId) {
  const { t } = useI18n();
  const faqs = ref([]);
  const isLoading = ref(false);
  let loadedAssistantId = null;

  const fetchFaqs = async (id, { force } = {}) => {
    if (!id) {
      faqs.value = [];
      loadedAssistantId = null;
      return;
    }
    if (!force && loadedAssistantId === id) return;

    isLoading.value = true;
    try {
      const { data } = await ArrowsAiFaqsAPI.get({ assistantId: id });
      faqs.value = (Array.isArray(data) ? data : []).map(normalizeFaq);
      loadedAssistantId = id;
    } catch (error) {
      faqs.value = [];
      loadedAssistantId = id;
      const message = parseAPIErrorResponse(error);
      useAlert(
        typeof message === 'string' ? message : t('ARROWS_AI.FAQS.FETCH_ERROR')
      );
    } finally {
      isLoading.value = false;
    }
  };

  watch(assistantId, id => fetchFaqs(id), { immediate: true });

  const createFaq = async payload => {
    try {
      const { data } = await ArrowsAiFaqsAPI.create({
        assistantId: assistantId.value,
        question: payload.question.trim(),
        answer: payload.answer.trim(),
      });
      const faq = normalizeFaq(data);
      faqs.value = [faq, ...faqs.value];
      return faq;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const updateFaq = async (id, payload) => {
    try {
      const { data } = await ArrowsAiFaqsAPI.update({
        assistantId: assistantId.value,
        id,
        question: payload.question.trim(),
        answer: payload.answer.trim(),
      });
      const updated = normalizeFaq(data);
      faqs.value = faqs.value.map(item =>
        item.id === updated.id ? updated : item
      );
      return updated;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const deleteFaqs = async ids => {
    const uniqueIds = [...new Set(ids.map(Number))];
    try {
      await Promise.all(
        uniqueIds.map(id =>
          ArrowsAiFaqsAPI.delete({ assistantId: assistantId.value, id })
        )
      );
      const idSet = new Set(uniqueIds);
      faqs.value = faqs.value.filter(item => !idSet.has(item.id));
    } catch (error) {
      await fetchFaqs(assistantId.value, { force: true });
      throwErrorMessage(error);
    }
  };

  const importFaqs = async file => {
    const { data } = await ArrowsAiFaqsAPI.import({
      assistantId: assistantId.value,
      file,
    });
    const imported = (Array.isArray(data.imported) ? data.imported : []).map(
      normalizeFaq
    );
    faqs.value = [...imported.slice().reverse(), ...faqs.value];
    return data;
  };

  return {
    faqs: computed(() => faqs.value),
    isLoading: computed(() => isLoading.value),
    createFaq,
    updateFaq,
    deleteFaqs,
    importFaqs,
  };
}
